import { toFixedNumber } from '@hive/util'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroll-component'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useMediaQuery } from 'usehooks-ts'

import { useMyBonusHistory } from '@entities/Loyalty/api/bonusHistory.gql'

import { cn } from '@shared/lib/cn'
import { toSafeDate } from '@shared/uttils'

import styles from './styles.module.scss'

const orderIdFromPurchaseId = (purchaseId?: string | null): string | null => {
  if (!purchaseId) return null
  const parts = purchaseId.split(';')
  return parts.length > 1 ? parts[1] : purchaseId
}

const mapOperationType = (
  type?: string | null,
  comment?: string | null,
  purchaseId?: string | null,
  t?: (key: string) => string,
): React.ReactNode => {
  if (!type) return <>error</>
  if (comment) return <>{comment}</>

  const orderNum = orderIdFromPurchaseId(purchaseId)

  switch (type) {
  case 'charge':
    return (
      <>
        {t?.('bonusOperationType.charge')}
      </>
    )
  case 'chargePaymentReturn':
    return <>{t?.('bonusOperationType.chargePaymentReturn')}</>
  case 'activate':
    return (
      <>
        {t?.('bonusOperationType.activate')}
        {orderNum ? (
          <>
            {' '}
            <span className={styles.greenText}>№ {orderNum}</span>
          </>
        ) : null}
      </>
    )
  case 'cancel':
    return <>{t?.('bonusOperationType.cancel')}</>
  case 'block':
    return <>{t?.('bonusOperationType.block')}</>
  case 'unblock':
    return <>{t?.('bonusOperationType.unblock')}</>
  case 'writeOff':
    return (
      <>
        {t?.('bonusOperationType.writeOff')}
      </>
    )
  case 'writeOffByReturn':
    return <>{t?.('bonusOperationType.writeOffByReturn')}</>
  case 'writeOffBlocked':
    return <>{t?.('bonusOperationType.writeOffBlocked')}</>
  case 'expire':
    return <>{t?.('bonusOperationType.expire')}</>
  case 'prolong':
    return <>{t?.('bonusOperationType.prolong')}</>
  case 'unknown':
    return <>{t?.('bonusOperationType.unknown')}</>
  default:
    return <>{type}</>
  }
}

export const BonusList: React.FC = () => {
  const isMobile = useMediaQuery('(max-width: 767px)')
  const { operations, fetchNextPage, hasMore } = useMyBonusHistory()
  const { t: t } = useTranslation('personal')

  const parsedBonuses =
    operations?.map((item) => {
      const isNegative = 
        item?.type === 'writeOff' || 
        item?.type === 'writeOffByReturn' || 
        item?.type === 'writeOffBlocked' || 
        item?.type === 'expire' ||
        item?.type === 'block'
      const parsedDate = toSafeDate(item?.operationAt)
      const date = parsedDate ? parsedDate.toLocaleDateString('ru-RU') : ''
      const Operation = mapOperationType(
        item?.type,
        item?.comment,
        item?.purchaseId,
        t,
      )

      return {
        amount: item?.amount || 0,
        isNegative,
        date,
        operation: Operation,
        postFix: item?.postFix || ''
      }
    }) || []

  return (
    <div className={styles.list}>
      {!isMobile && (
        <div className={styles.list__head}>
          <span>{t('bonusHistory.bonuses')}</span>
          <span>{t('bonusHistory.date')}</span>
          <span>{t('bonusHistory.type')}</span>
        </div>
      )}
      <div className={styles.list__body}>
        <InfiniteScroll
          className={styles.list__content}
          dataLength={parsedBonuses.length}
          next={fetchNextPage}
          hasMore={hasMore}
          loader={
            <SkeletonTheme>
              <Skeleton
                count={3}
                height={isMobile ? 100 : 60}
                style={{ borderRadius: 12, marginBottom: 8 }}
              />
            </SkeletonTheme>
          }
        >
          {parsedBonuses.map((item, i) =>
            isMobile ? (
              <div className={styles.card} key={`${item.amount}-${i}`}>
                <div>
                  <p>{t('bonusHistory.bonuses')} </p>
                  <span
                    className={cn(
                      styles.card__amount,
                      item.isNegative ? styles.redText : styles.greenText,
                    )}
                  >
                    {item.isNegative ? '-' : '+'}
                    {item.amount > 1 ? toFixedNumber(item.amount, 0) : item.amount} {item.postFix}
                  </span>
                </div>
                <div>
                  <p>{t('bonusHistory.date')} </p>
                  <span> {item.date}</span>
                </div>
                <div>
                  <p>{t('bonusHistory.type')} </p>
                  <span> {item.operation}:</span>
                </div>
              </div>
            ) : (
              <div className={styles.list__item} key={`${item.amount}-${i}`}>
                <div className={styles.list__amount}>
                  <p
                    className={
                      item.isNegative ? styles.redText : styles.greenText
                    }
                  >
                    {item.isNegative ? '-' : '+'}
                    {item.amount > 1 ? toFixedNumber(item.amount, 0) : item.amount} {item.postFix}
                  </p>
                  {/* <span>~12 ₸</span> */}
                </div>
                <span>{item.date}</span>
                <div className={styles.list__operation}>
                  <p>
                    {item.operation}
                  </p>
                </div>
              </div>
            ),
          )}
        </InfiniteScroll>
      </div>
    </div>
  )
}
